import axios from 'axios';
import http from './user';
import paymentHttp from './payment';
import paymentV2Http from './payment-v2';
import httpFile from './file';
import httpGroup from './ping-group';
import { getDeviceId } from '../utils/device';
import { StringUtils } from '../utils/string';
import { ServiceType, UserStatus } from '../types/user';
import { BusinessQuota, ServiceLevel } from '../types/business';
import { StripeSubscriptionStatus } from '../types/subscription';
import { StripeTransactionStatus, TPayment } from '../types/payment';

export { StripeSubscriptionStatus } from '../types/subscription';
export { StripeTransactionStatus } from '../types/payment';

const refreshHttp = axios.create({
  baseURL: http.defaults.baseURL,
});


export const getPingPadUsers = (data: { page: number; pageSize: number; name?: string; emailOrPhone?: string; statuses?: string; paymentStatuses?: string; sort: string }) => {
  const url = StringUtils.addParamsToUrl('/users', {
    serviceType: ServiceType.PINGPAD,
    ...data,
  });
  return http.get(url);
};

export const getUser = (userId: string, populateBusiness: boolean) => {
  return http.get(StringUtils.addParamsToUrl(`/users/${userId}`, { populateBusiness }));
};

export const getUserMetadata = (userId: string) => {
  return http.get(StringUtils.addParamsToUrl(`/users/${userId}/metadata`, {}));
};

export const updateUserStatus = (userId: string, status: UserStatus, blockReason?: string, deletionReason?: string) => {
  const body: { status: UserStatus; blockReason?: string; deletionReason?: string } = { status };
  if (blockReason !== undefined) body.blockReason = blockReason;
  if (deletionReason !== undefined) body.deletionReason = deletionReason;
  return http.patch(`/users/${userId}/status`, body);
};

export const approveUser = (userId: string) => {
  return http.patch(`/users/${userId}/approve`);
};

export const logout = () => {
  return http.post('/users/logout', {});
};

export const login = (data: any) => {
  return http.post('/users/login', {
    ...data,
    device: {
      type: 'web',
      deviceId: getDeviceId(),
      ipAddr: '1.2.3.4',
      userAgent: navigator.userAgent,
    },
  });
};

export const resendConfirmEmail = (data: any) => {
  return http.patch('/special-groups/resend-confirm-email', data);
};

export const phoneLogin = (data: any) => {
  return http.post('/users/login-phone', data);
};

export const resendPhonePasscode = (data: any) => {
  return http.post('/users/resend-phone-passcode', data);
};

export const verifyPhonePasscode = (data: any) => {
  return http.post('/users/verify-phone-passcode', data);
};

export const verifyPhoneAccount = (data: any) => {
  return http.post('/users/verify-account', {
    ...data,
    device: {
      type: 'sircle-app',
      deviceId: '1234',
      model: 'postman',
      OS: 'postman',
      OSVersion: 'postman',
      locale: 'en-us',
      country: 'US',
      appVersionCode: '0',
      appVersionName: '0',
      timezone: 'unknown',
      gcmToken: null,
    },
    silent: true,
  });
};

export const forgotPassword = (data: any) => {
  return http.put('/users/forgot-password', data);
};

export const resetPassword = (data: any) => {
  return http.put('/users/reset-password', data);
};

export const updatePassword = (data: any) => {
  return http.put('/users/password', data);
};

export const updateUserProfile = (data: any) => {
  return http.put('/users/profile', data);
};

export const refreshToken = async (
  refreshToken: string,
) => {
  return refreshHttp.patch(
    '/users/access-token',
    undefined,
    {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    },
  );
};

export const getCurrentUser = () => {
  return http.get('/users/profile');
};

export const checkExistEmail = (email: string, serviceType: ServiceType, includePendingUser = false) => {
  return http.get(StringUtils.addParamsToUrl(`/users/exists/email/${email}`, { serviceType, includePendingUser }));
};

export const checkEmailAccounts = (email: string) => {
  return http.get(StringUtils.addParamsToUrl(`/users/email-accounts/${email}`, {}));
};

export const /**/ register = (data: any) => {
    return http.post('/special-groups/register', data);
  };

export const assignGroup = (data: any) => {
  return http.patch('/special-groups/assign', data);
};

export const createGroup = (data: any) => {
  return http.patch('/special-groups/create-group', data);
};

export const updateGroupSettings = ({ groupType, groupId, settings }: any) => {
  return http.patch(`/special-groups/${groupType}/${groupId}/settings`, { settings });
};

// deprecated, retest before using
export const assignExistingGroup = (data: any) => {
  return http.patch('/special-groups/assign-existing', data);
};

export const updateBusinessInfo = ({ data, type, id }: any) => {
  return http.patch(`/special-groups/${type}/${id}`, { [type]: data });
};

export const updateBusinessQuota = (businessId: string, quota: BusinessQuota) => {
  return updateBusinessInfo({ type: 'business', id: businessId, data: { quotaOverrides: quota } });
};

export const addMember = ({ data, type, id }: any) => {
  return http.post(`/special-groups/${type}/${id}/members`, data);
};

export const bulkRemoveMember = ({ type, id, tranId }: any) => {
  return http.delete(`/special-groups/${type.toLowerCase()}/${id}/members/bulk/${tranId}`);
};

export const updateMembers = ({ type, id, members }: any) => {
  return http.patch(`/special-groups/${type.toLowerCase()}/${id}/members/bulk`, { members });
};

export const updateMember = ({ groupId, type, userId, data }: any) => {
  return http.patch(`/special-groups/${type.toLowerCase()}/${groupId}/members/${userId}`, data);
};

export const removeMember = ({ userId, type, id }: any) => {
  return http.delete(`/special-groups/${type}/${id}/members/` + userId);
};

export const getMembers = ({ type, id }: any) => {
  return http.get(`/special-groups/${type}/${id}/members`);
};

export const getPaymentClientToken = () => {
  return paymentHttp.get(`/braintree/client-token`);
};

/** Plan from GET /api/v1/plans API. Replaces deprecated /premium-packages. */
export interface PlanAddOn {
  id: string;
  name: string;
  amount: string;
  description?: string;
  kind?: string;
  neverExpires?: boolean;
  numberOfBillingCycles?: number | null;
  merchantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanFromAPI {
  id: string;
  name: string;
  price: string;
  currencyIsoCode?: string;
  description?: string;
  billingFrequency?: number;
  billingDayOfMonth?: number | null;
  numberOfBillingCycles?: number | null;
  trialPeriod?: boolean;
  trialDuration?: number | null;
  trialDurationUnit?: string | null;
  merchantId?: string;
  createdAt?: string;
  updatedAt?: string;
  addOns: PlanAddOn[];
  discounts: unknown[];
}

export interface StripePlanFromAPI {
  key: string;
  serviceLevel: ServiceLevel;
  extensionPriceId?: string;
  extensionAmount: number;
  features: string[];
  currency: 'usd';
  interval: 'month';
}

export const getPlans = (): Promise<{ resultCode: number; errMessage: string | null; data: { plans: PlanFromAPI[] } }> => {
  return paymentHttp.get('/plans');
};

export const getStripePlans = (): Promise<{ resultCode: number; errMessage: string | null; data: { plans: StripePlanFromAPI[] } }> => {
  return paymentV2Http.get('/plans');
};

export type AddOnFromAPI = PlanAddOn;

export const getAddOns = () => {
  return paymentHttp.get('/addons');
};

export const getPaymentMethods = () => {
  return paymentHttp.get('/customers/payment-methods');
};

export interface StripePaymentMethod {
  id: string;
  type: 'card';
  customer?: string;
  billing_details?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  card?: {
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
  created?: number;
  isDefault?: boolean;
}

export const mapStripePaymentMethodToPayment = (method: StripePaymentMethod, index = 0): TPayment => {
  const brand = method.card?.brand || 'Card';
  const normalizedBrand = brand
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  return {
    ...method,
    token: method.id,
    default: method.isDefault ?? index === 0,
    cardType: normalizedBrand,
    cardholderName: method.billing_details?.name || '',
    email: method.billing_details?.email || '',
    maskedNumber: method.card?.last4 ? `************${method.card.last4}` : '',
    expirationMonth: method.card?.exp_month?.toString().padStart(2, '0') || '',
    expirationYear: method.card?.exp_year?.toString() || '',
    createdAt: method.created ? new Date(method.created * 1000).toISOString() : undefined,
    subscriptions: [],
  };
};

export const createStripeSetupIntent = (): Promise<{ resultCode: number; errMessage: string | null; data: { setupIntentId: string; clientSecret: string } }> => {
  return paymentV2Http.post('/customers/payment-methods/setup-intent');
};

export const getStripePaymentMethods = (): Promise<{ resultCode: number; errMessage: string | null; data: { paymentMethods: StripePaymentMethod[] } }> => {
  return paymentV2Http.get('/customers/payment-methods');
};

export const updateStripePaymentMethod = (paymentMethodId: string, data: { makeDefault?: boolean } = {}) => {
  return paymentV2Http.patch(`/customers/payment-methods/${paymentMethodId}`, data);
};

export const deleteStripePaymentMethod = (paymentMethodId: string) => {
  return paymentV2Http.delete(`/customers/payment-methods/${paymentMethodId}`);
};

export const createStripePaymentMethod = (data: { paymentMethodId: string; makeDefault?: boolean }) => {
  return paymentV2Http.post('/customers/payment-methods', data);
};

export type VisitorAccessProductType = 'QR_BOARD' | 'KIOSK';

export interface VisitorAccessOrderItem {
  productType: VisitorAccessProductType;
  quantity: number;
  unitAmount: number;
  shippingAmount: number;
  totalAmount: number;
}

export interface VisitorAccessOrder {
  _id: string;
  businessId: string;
  provider?: 'braintree' | 'stripe';
  items?: VisitorAccessOrderItem[];
  productType?: VisitorAccessProductType;
  quantity?: number;
  unitPriceUsd?: number;
  subtotalAmount?: number;
  shippingAmount?: number;
  totalAmount?: number;
  totalAmountUsd?: number;
  currency?: string;
  braintreeTransactionId?: string;
  stripePaymentIntentId?: string;
  status: 'CREATED' | 'PENDING' | 'SUCCESS' | 'FAILED';
  createdTime: string;
}

export const getVisitorAccessOrders = (businessId: string) => {
  return paymentHttp.get(StringUtils.addParamsToUrl('/customers/visitor-access-orders', { businessId }));
};

export const createVisitorAccessOrder = (data: {
  businessId: string;
  productType: VisitorAccessProductType;
  quantity: number;
  paymentMethodToken?: string;
  paymentMethodNonce?: string;
}) => {
  return paymentHttp.post('/customers/visitor-access-orders', data);
};

export const getStripeVisitorAccessOrders = (businessId: string) => {
  return paymentV2Http.get(StringUtils.addParamsToUrl('/customers/visitor-access-orders', { businessId }));
};

export const createStripeVisitorAccessOrder = (data: {
  businessId: string;
  items: Array<{
    productType: VisitorAccessProductType;
    quantity: number;
  }>;
  paymentMethodId?: string;
}) => {
  return paymentV2Http.post('/customers/visitor-access-orders', data);
};

export const createPaymentMethod = (data: any) => {
  return paymentHttp.post('/customers/payment-methods', data);
};

export const updatePaymentMethod = (token: string, updates: any) => {
  return paymentHttp.patch(`/customers/payment-methods/${token}`, {
    updates,
  });
};

export const updateSubscriptionPaymentMethod = (
  subscriptionId: string,
  data: {
    paymentMethodNonce?: string;
    paymentMethodToken?: string;
  },
) => {
  return paymentHttp.patch(`/customers/subscriptions/${subscriptionId}/payment`, data);
};

export const updateStripeSubscriptionPaymentMethod = (
  subscriptionId: string,
  data: {
    paymentMethodId: string;
  },
) => {
  return paymentV2Http.patch(`/customers/subscriptions/${subscriptionId}/payment`, data);
};

export const deletePaymentMethod = (token: string) => {
  return paymentHttp.delete(`/customers/payment-methods/${token}`);
};

export const getSubscriptions = () => {
  return paymentHttp.get('/customers/subscriptions');
};

export interface StripeSubscription {
  _id: string;
  businessId: string;
  groupType: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripeExtensionItemId?: string;
  stripeExtensionPriceId?: string;
  serviceLevel: ServiceLevel;
  monthlyPeakExtensionCount?: number;
  status: StripeSubscriptionStatus;
  purchased: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  latestInvoiceId?: string;
  latestPaymentIntentId?: string;
  createdTime?: string;
  updatedTime?: string;
  /** @deprecated Braintree legacy subscription id */
  id?: string;
  /** @deprecated Braintree legacy plan id */
  planId?: string;
}

export interface StripeTransaction {
  _id: string;
  owner?: string;
  businessId?: string;
  transactionNumber?: string;
  stripeInvoiceId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  stripeSubscriptionId?: string;
  amount: number;
  currency: string;
  status: StripeTransactionStatus;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  periodStart?: string;
  periodEnd?: string;
  paymentMethod?: string;
  paymentMethodDetails?: Record<string, any>;
  createdTime: string;
  updatedTime: string;
  updatedBy?: string | null;
}

export const getStripeSubscriptions = (businessId?: string) => {
  return paymentV2Http.get(StringUtils.addParamsToUrl('/customers/subscriptions', { businessId }));
};

export const createSubscription = (data: any) => {
  return paymentHttp.post('/customers/subscriptions', data);
};

export const createStripeSubscription = (data: { groupId: string; type: string; serviceLevel: ServiceLevel; paymentMethodId?: string }) => {
  return paymentV2Http.post('/customers/subscriptions', data);
};

export const syncStripeSubscriptionEntitlements = (subscriptionId: string, businessId: string) => {
  return paymentV2Http.patch(`/customers/subscriptions/${subscriptionId}/sync`, { businessId });
};

export const cancelSubscription = (subscriptionId: string) => {
  return paymentHttp.delete(`/customers/subscriptions/${subscriptionId}`);
};

export const cancelStripeSubscription = (subscriptionId: string, businessId: string) => {
  return paymentV2Http.delete(`/customers/subscriptions/${subscriptionId}`, { data: { businessId } });
};

export const updateSubscription = (subscriptionId: any, data: any) => {
  return paymentHttp.patch(`/customers/subscriptions/${subscriptionId}`, data);
};

export const updateStripeSubscription = (
  subscriptionId: string,
  data: {
    businessId?: string;
    serviceLevel: ServiceLevel;
    endTrialNow?: boolean;
  },
) => {
  return paymentV2Http.patch(`/customers/subscriptions/${subscriptionId}`, data);
};

export const updateSubscriptionAutoRenewal = (subscriptionId: string, data: { autoRenewal: boolean }) => {
  return paymentV2Http.patch(`/customers/subscriptions/${subscriptionId}/auto-renewal`, data);
};

export const getPaymentMethodByToken = (paymentMethodToken: string) => {
  return paymentHttp.get(`/customers/payment-methods/${paymentMethodToken}`);
};

export const setPurchased = ({ data, type, id }: any) => {
  return http.patch(`/special-groups/${type}/${id}/purchased`, data);
};

export const deactiveGroup = ({ type, id }: any) => {
  return http.delete(`/special-groups/${type}/${id}`);
};

export const activateBusiness = ({ id }: any) => {
  return http.post(`/special-groups/activate-business`, { id });
};

export const setPremium = (userId: any, data: any) => {
  return http.patch(`/users/${userId}/premium`, data);
};

export const applyDiscountCode = (discountCode: any) => {
  return paymentHttp.get(`/discount?discountCode=${discountCode}`);
};

export const getUserTransactions = (): Promise<{ resultCode: number; errMessage: string | null; data: { transactions: StripeTransaction[] } }> => {
  return paymentV2Http.get('/customers/transactions');
};

export const getTransactionById = (transactionId: string): Promise<{ resultCode: number; errMessage: string | null; data: { transaction: StripeTransaction } }> => {
  return paymentV2Http.get(`/customers/transactions/${transactionId}`);
};

export const loginMasquerade = (groupType: any, groupId: any, userId: any) => {
  return http.post('/users/login-masquerade', { groupType, groupId, userId });
};

export const getSignedUrl = async (fileName: any, size: any) => {
  const {
    data: { s3url },
  } = await httpFile.get(`/v2/files/s3/${fileName}?size=${size}&digest=unknown`);
  return s3url;
};

export const uploadToS3 = async (signedUrl: any, file: any) => {
  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};
export function getGroups() {
  throw new Error('Function not implemented.');
}

// Subscriptions
export const setSubscriptionStatus = (data: any) => {
  return http.patch(`/special-groups/business/${data.id}/status`, { status: data.status });
};

export const hardDeleteSubscription = ({ id, type }: any) => {
  return http.delete(`/special-groups/delete/${type.toLowerCase()}/${id}`);
};

// Bulletin board
export const createNews = (data: any) => {
  return httpGroup.post('/posts', data);
};

export const newsList = (params: any) => {
  return httpGroup.get(StringUtils.addParamsToUrl('/posts', params));
};

export const newsDetail = (id: any) => {
  return httpGroup.get(`/posts/${id}`);
};

export const newsEdit = (data: any) => {
  return httpGroup.put(`/posts/${data.id}`, data);
};

export const newsDelete = (id: any) => {
  return httpGroup.delete(`/posts/${id}`);
};

export const archivePost = (id: any) => {
  return httpGroup.patch(`/posts/${id}/archive`);
};

export const unpublishPost = (id: any) => {
  return httpGroup.patch(`/posts/${id}/unpublish`);
};

export const publishPost = (id: any) => {
  return httpGroup.put(`/posts/${id}`, { status: 'PUBLISHED' });
};

export const getPostStats = (id: any) => {
  return httpGroup.get(`/posts/${id}/stats`);
};

export const getPostHistory = (id: any) => {
  return httpGroup.get(`/posts/${id}/history`);
};

export const recordPostContact = (id: string, deviceId: string) => {
  return httpGroup.post(`/posts/${id}/contact`, { deviceId });
};

export type GetPostsParams = {
  page: number;
  pageSize: number;
  search?: string;
  sort?: string;
  category?: string; // comma-separated values
  status?: string; // comma-separated values
};

export const getPosts = (params: GetPostsParams) => {
  return httpGroup.get(StringUtils.addParamsToUrl('/posts', params));
};
